/**
 * Phase 2E-4 API Integration Hook
 * 
 * Provides React hooks for GM Strategy Hub API integration.
 * Replaces mock data with real backend calls.
 * 
 * NEW in 2E-4:
 *   - useFieldOverview(poolId) - Field analytics for Strategy tab
 *   - useCompare(poolId, opponentUserId) - 1v1 Battle comparison
 *   - useUserPerformance() - My Performance history
 *   - Enhanced useLeaderboard with error states
 * 
 * FIXED: Resolves string pool_id to integer id for /slate endpoint
 * 
 * Usage:
 *   const { slate, loading, error } = usePoolSlate(poolId);
 *   const { entry, state, saveEntry, lockEntry } = useUserEntry(poolId, userId);
 *   const { fieldData, loading, error } = useFieldOverview(poolId);
 *   const { compareData, loading, error, compare } = useCompare(poolId);
 *   const { performance, loading, error } = useUserPerformance();
 */

import { useState, useEffect, useCallback } from 'react';
import { normalizePlayer } from '../utils/transformers';
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// TypeScript interfaces
interface SlatePlayer {
  id: number;
  name: string;
  team: string;
  pos: string;
  opp?: string;
  avgYds?: number;
  propYds?: number;
  ydsEdge?: number;
  avgTDs?: number;
  propTDs?: number;
  tdsEdge?: number;
  avgComp?: number;
  propComp?: number;
  compEdge?: number;
  passDefRank?: number;
  runDefRank?: number;
  overallRank?: number;
  props?: any[];
}

interface SlateData {
  pool: any;
  players: SlatePlayer[];
  teams?: any;
}



/**
 * Fetch pool slate (players, props, teams)
 * FIXED: Resolves string pool_id to integer id before calling /slate
 */
export function usePoolSlate(poolId: number | string | null) {
  const [slate, setSlate] = useState<SlateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!poolId) {
      setSlate(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Use string pool_id directly - backend accepts string (Phase 3H-U fix)
    const resolveAndFetchSlate = async (): Promise<SlateData> => {
      const slateRes = await fetch(`${API_BASE}/pools/${poolId}/slate`);
      if (!slateRes.ok) throw new Error(`HTTP ${slateRes.status}`);
      return slateRes.json();
    };
resolveAndFetchSlate()
      .then(data => {
        setSlate(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setLoading(false);
      });
  }, [poolId]);

  return { slate, loading, error };
}
/**
 * Fetch and manage user entry for a pool
 */
export function useUserEntry(poolId: number | string | null, userId: string) {
  const [entry, setEntry] = useState<any>(null);
  const [state, setState] = useState('NOT_ENTERED');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load entry on mount
  useEffect(() => {
    if (!poolId) return;

    setLoading(true);
    fetch(`${API_BASE}/pools/${poolId}/entry`, {
      headers: { 'X-User-ID': userId }
    })
      .then(res => {
        if (!res.ok) {
          if (res.status === 404) {
            // No entry yet - initialize empty
            setEntry({ roster: [] });
            setState('NOT_ENTERED');
            setLoading(false);
            return null;
          }
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        if (data) {
          setEntry(data.entry);
          setState(data.state || 'DRAFT');
        }
        setLoading(false);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setLoading(false);
      });
  }, [poolId, userId]);

  // Save roster (draft)
  const saveEntry = useCallback(async (roster: any[]) => {
    if (!poolId) return { success: false, errors: ['No pool selected'] };

    setSaving(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/pools/${poolId}/entry/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-ID': userId },
        body: JSON.stringify({ roster })
      });
      
      const data = await res.json();
      
      if (data.success) {
        // Refresh entry
        const entryRes = await fetch(`${API_BASE}/pools/${poolId}/entry`, {
          headers: { 'X-User-ID': userId }
        });
        const entryData = await entryRes.json();
        setEntry(entryData.entry);
        setState(entryData.state || 'DRAFT');
      }
      
      setSaving(false);
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      setSaving(false);
      return { success: false, errors: [message] };
    }
  }, [poolId, userId]);

  // Lock roster
  const lockEntry = useCallback(async () => {
    if (!poolId) return { success: false, errors: ['No pool selected'] };

    setSaving(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/pools/${poolId}/entry/lock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-ID': userId }
      });
      
      const data = await res.json();
      
      if (data.success) {
        setState('LOCKED');
        // Refresh entry
        const entryRes = await fetch(`${API_BASE}/pools/${poolId}/entry`, {
          headers: { 'X-User-ID': userId }
        });
        const entryData = await entryRes.json();
        setEntry(entryData.entry);
      }
      
      setSaving(false);
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      setSaving(false);
      return { success: false, errors: [message] };
    }
  }, [poolId, userId]);

  return { entry, state, loading, saving, error, saveEntry, lockEntry };
}

/**
 * Fetch pool state for navigation control
 */
export function usePoolState(poolId: number | string | null, userId: string) {
  const [poolState, setPoolState] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!poolId) {
      setPoolState(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    fetch(`${API_BASE}/pools/${poolId}/state`, {
      headers: { 'X-User-ID': userId }
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        setPoolState(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setLoading(false);
      });
  }, [poolId, userId]);

  const refresh = useCallback(() => {
    if (!poolId) return;
    fetch(`${API_BASE}/pools/${poolId}/state`, {
      headers: { 'X-User-ID': userId }
    })
      .then(res => res.json())
      .then(setPoolState);
  }, [poolId, userId]);

  return { poolState, loading, error, refresh };
}

/**
 * Fetch leaderboard/standings with enhanced error handling
 */
export function useLeaderboard(poolId: number | string | null, limit: number = 50) {
  const [standings, setStandings] = useState<any[]>([]);
  const [poolInfo, setPoolInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!poolId) {
      setStandings([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    fetch(`${API_BASE}/pools/${poolId}/leaderboard?limit=${limit}`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        setStandings(data.standings || []);
        setPoolInfo({
          poolName: data.poolName,
          totalEntrants: data.totalEntrants
        });
        setLoading(false);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setLoading(false);
      });
  }, [poolId, limit]);

  const refresh = useCallback(() => {
    if (!poolId) return;
    setLoading(true);
    fetch(`${API_BASE}/pools/${poolId}/leaderboard?limit=${limit}`)
      .then(res => res.json())
      .then(data => {
        setStandings(data.standings || []);
        setPoolInfo({
          poolName: data.poolName,
          totalEntrants: data.totalEntrants
        });
        setLoading(false);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setLoading(false);
      });
  }, [poolId, limit]);

  return { standings, poolInfo, loading, error, refresh };
}

// ============================================================================
// PHASE 2E-4 NEW HOOKS
// ============================================================================

/**
 /**
 * FIXED useFieldOverview - Replace lines 347-393 in useGMStrategy.ts
 * 
 * Fixes:
 * 1. Adds X-User-ID header (422 fix)
 * 2. Transforms backend field names to UI expectations
 */

/**
 * Fetch field overview analytics for Strategy Hub
 */
export function useFieldOverview(poolId: number | string | null, userId: string) {
  const [fieldData, setFieldData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!poolId) {
      setFieldData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    fetch(`${API_BASE}/pools/${poolId}/field/overview`, {
      headers: { 'X-User-ID': userId }
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        // Transform backend response to UI format
        const transformed = {
          totalEntrants: data.totalEntries ?? data.totalEntrants ?? 0,
          lockedEntrants: data.lockedEntries ?? data.lockedEntrants ?? data.totalEntries ?? 0,
          completionPct: data.completionPct ?? (data.totalEntries > 0 ? 100 : 0),
          avgUnitsUsed: data.avgUnitsUsed ?? data.avgUnits ?? 0,
          avgFilledSlots: data.avgFilledSlots ?? data.avgSlots ?? 7,
          popularPlayers: (data.popularPicks || data.playerOwnership || []).slice(0, 5).map((p: any) => ({
            playerId: p.playerId,
            name: p.playerName || p.name,
            ownership: Math.round(p.ownershipPct ?? p.ownership ?? 0)
          }))
        };
        setFieldData(transformed);
        setLoading(false);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setLoading(false);
      });
  }, [poolId, userId]);

  const refresh = useCallback(() => {
    if (!poolId) return;
    setLoading(true);
    fetch(`${API_BASE}/pools/${poolId}/field/overview`, {
      headers: { 'X-User-ID': userId }
    })
      .then(res => res.json())
      .then(data => {
        // Transform backend response to UI format
        const transformed = {
          totalEntrants: data.totalEntries ?? data.totalEntrants ?? 0,
          lockedEntrants: data.lockedEntries ?? data.lockedEntrants ?? data.totalEntries ?? 0,
          completionPct: data.completionPct ?? (data.totalEntries > 0 ? 100 : 0),
          avgUnitsUsed: data.avgUnitsUsed ?? data.avgUnits ?? 0,
          avgFilledSlots: data.avgFilledSlots ?? data.avgSlots ?? 7,
          popularPlayers: (data.popularPicks || data.playerOwnership || []).slice(0, 5).map((p: any) => ({
            playerId: p.playerId,
            name: p.playerName || p.name,
            ownership: Math.round(p.ownershipPct ?? p.ownership ?? 0)
          }))
        };
        setFieldData(transformed);
        setLoading(false);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setLoading(false);
      });
  }, [poolId]);

  return { fieldData, loading, error, refresh };
}

/**
 * Compare rosters for 1v1 Battle What-If
 */
export function useCompare(poolId: number | string | null, userId: string) {
  const [compareData, setCompareData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const compare = useCallback(async (opponentUserId: string) => {
    if (!poolId || !opponentUserId) {
      setError('Pool ID and opponent required');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/pools/${poolId}/compare`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-User-ID': userId 
        },
        body: JSON.stringify({ opponentUserId })
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setCompareData(data);
      setLoading(false);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setLoading(false);
      return null;
    }
  }, [poolId, userId]);

  const clearCompare = useCallback(() => {
    setCompareData(null);
    setError(null);
  }, []);

  return { compareData, loading, error, compare, clearCompare };
}

/**
 * Fetch user performance history
 */
export function useUserPerformance(userId: string) {
  const [performance, setPerformance] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetch(`${API_BASE}/user/performance`, {
      headers: { 'X-User-ID': userId }
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        setPerformance(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setLoading(false);
      });
  }, [userId]);

  const refresh = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/user/performance`, {
      headers: { 'X-User-ID': userId }
    })
      .then(res => res.json())
      .then(data => {
        setPerformance(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setLoading(false);
      });
  }, [userId]);

  return { performance, loading, error, refresh };
}

// ============================================================================
// TRANSFORM UTILITIES
// ============================================================================

/**
 * Transform API slate data to match mock data format
 * This bridges the gap between API response and existing UI expectations
 */

export function transformSlateToPlayers(slate: SlateData | null) {
  if (!slate?.players) return [];
  return slate.players.map(normalizePlayer);
}

/**
 * Transform API pool data to match mock pool format
 */
export function transformPoolData(slatePool: any) {
  if (!slatePool) return null;
  
  return {
    id: slatePool.id,
    name: slatePool.name,
    slots: slatePool.slots || 7,
    units: slatePool.units || 100,
    deadline: slatePool.deadline,
    kickoff: slatePool.kickoff,
    status: slatePool.status || 'open',
    rulesConfig: slatePool.rulesConfig
  };
}

/**
 * Build roster array for API save call
 */
export function buildRosterPayload(roster: any[]) {
  return roster.map(slot => ({
    slot: slot.slot,
    label: slot.label,
    player: slot.player ? {
      id: slot.player.id,
      name: slot.player.name,
      team: slot.player.team,
      pos: slot.player.pos
    } : null,
    prop: slot.prop ? {
      id: slot.prop.id,
      stat: slot.prop.stat,
      line: slot.prop.line,
      playerAvg: slot.prop.playerAvg || 0,
      edge: slot.prop.edgeValue ?? slot.prop.edge ?? 0
    } : null,
    side: slot.side,
    units: slot.units || 0
  }));
}

/**
 * Transform leaderboard entry to UI standing format
 */
export function transformStandingToUI(standing: any, myUserId: string) {
  return {
    rank: standing.rank,
    username: standing.username || standing.userId,
    userId: standing.userId,
    score: standing.score || 0,
    totalUnits: standing.totalUnits || 0,
    isLocked: standing.isLocked,
    status: standing.status,
    isYou: standing.userId === myUserId,
    // UI-specific fields with defaults
    style: standing.totalUnits > 80 ? 'Concentrated' : 'Balanced',
    streak: standing.isLocked ? 'Locked' : 'Building'
  };
}

export default {
  usePoolSlate,
  useUserEntry,
  usePoolState,
  useLeaderboard,
  useFieldOverview,
  useCompare,
  useUserPerformance,
  transformSlateToPlayers,
  transformPoolData,
  buildRosterPayload,
  transformStandingToUI
};
